import CryptoJS from "crypto-js";

/**
 * Builds a signed, single-use HIMS Pharmacy Billing SSO url from the current
 * session/UI data (no backend call).
 *
 * ⚠️ SECURITY NOTE: this signs the token IN THE BROWSER, so the shared
 * PROVIDER_SSO_SECRET lives in the client bundle — anyone who extracts it can
 * forge SSO tokens for any user. (Same trade-off as the app's existing frontend
 * Scribe/Analyze SSO keys.) For production hardening, sign on the backend so the
 * secret never reaches the browser — see docs/hms-pharmacy-sso/.
 *
 * URL: <HMS_BASE>/site/providerSso?blob=<base64url(json)>.<hmac_sha256_hex>
 */

const HIMS_BASE =
  process.env.REACT_APP_HMS_BASE_URL || "https://hms-qa.meragoapp.com";

const base64url = (str) =>
  CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

/**
 * @param {object} data
 * @param {string} data.u   Merago user id (required)
 * @param {string} data.org provider-org id HIMS maps (required)
 * @param {string} [data.nm] display name (first visit)
 * @param {string} [data.em] email (first visit)
 * @param {string} [data.mb] mobile
 * @param {string} [data.branch]    pharmacy branch code/name (omit → org default)
 * @param {string} [data.patientId] patient's Merago user id
 * @param {string} [data.rx]        prescription / encounter ref
 * @returns {string|null} signed url, or null if not configured / missing data
 */
export const buildPharmacySsoUrl = ({
  u,
  org,
  nm,
  em,
  mb,
  branch,
  patientId,
  rx,
} = {}) => {
  const secret = process.env.REACT_APP_HMS_PROVIDER_SSO_SECRET;
  if (!secret || !u || !org) {
    // eslint-disable-next-line no-console
    console.warn("[HMS Pharmacy SSO] not built — missing:", {
      hasSecret: !!secret,
      u,
      org,
    });
    return null; // not configured / missing identity
  }

  const payload = {
    org,
    u,
    exp: Math.floor(Date.now() / 1000) + 120, // ≤120s ahead (600s hard cap)
    n: CryptoJS.lib.WordArray.random(16).toString(CryptoJS.enc.Hex), // fresh nonce
  };
  if (nm) payload.nm = nm;
  if (em) payload.em = em;
  if (mb) payload.mb = mb;
  if (branch) payload.b = branch;
  if (patientId) payload.p = patientId;
  if (rx) payload.rx = rx;

  const b64 = base64url(JSON.stringify(payload));
  const sig = CryptoJS.HmacSHA256(b64, secret).toString(CryptoJS.enc.Hex);
  const url = `${HIMS_BASE}/site/providerSso?blob=${b64}.${sig}`;

  // eslint-disable-next-line no-console
  console.log("[HMS Pharmacy SSO] org:", payload.org);
  // eslint-disable-next-line no-console
  console.log("[HMS Pharmacy SSO] full payload:", JSON.stringify(payload));
  // eslint-disable-next-line no-console
  console.log("[HMS Pharmacy SSO] payload (object):", payload);
  // eslint-disable-next-line no-console
  console.log("[HMS Pharmacy SSO] url:", url);

  return url;
};
